import { Checkbox, Flex, RadioGroup, Text, TextField } from '@radix-ui/themes'
import { useMemo, useState } from 'react'
import { PrizeRevealMethod, S2WGame, S2WTheme } from '@Generated/back-office-client/src'
import { EditDrawerFrame } from './edit-drawer-frame'
import { GameFormData, PrizeScheduleForm } from './form'
import { Field, FieldError } from './form-fields'
import {
  PRIZE_REVEAL_METHODS,
  prizeRevealMethodLabel,
  S2W_THEMES,
  supportsDoubleWheel,
} from './types'
import { useSaveGame } from './use-save-game'

type GameEditDrawerProps = {
  game: S2WGame
  onClose: () => void
}

type NumberField = 'pointCost' | 'promoTextLimit' | 'wheelTextLimit'

export const GameEditDrawer = ({ game, onClose }: GameEditDrawerProps) => {
  const [form, setForm] = useState<GameFormData>(() => PrizeScheduleForm.toGameForm(game))
  const errors = useMemo(() => PrizeScheduleForm.validateGame(form), [form])
  const { save, saveError, isSaving } = useSaveGame(game.id, onClose)
  // The theme and reveal method limit each other: a single-wheel-only theme can't be picked while the reveal is
  // double wheel, and double wheel can't be picked for such a theme
  const doubleWheel = form.prizeRevealMethod === PrizeRevealMethod.DOUBLE_WHEEL_PRIZE_REVEAL

  const numberInput = (field: NumberField, label: string) => (
    <Field label={label}>
      <TextField.Root
        aria-label={label}
        inputMode="numeric"
        value={form[field]}
        onChange={(e) => setForm({ ...form, [field]: e.target.value })}
      />
      <FieldError errors={errors} path={field} />
    </Field>
  )

  return (
    <EditDrawerFrame
      title={`Edit ${game.name} settings`}
      saveError={saveError}
      isSaving={isSaving}
      canSave={Object.keys(errors).length === 0}
      onSave={() => save(PrizeScheduleForm.toGameUpdate(form))}
      onClose={onClose}>
      <Field label="Name">
        <TextField.Root
          aria-label="Name"
          value={form.name}
          onChange={(e) => setForm({ ...form, name: e.target.value })}
        />
        <FieldError errors={errors} path="name" />
      </Field>
      {numberInput('pointCost', 'Spin cost (points)')}
      <Field label="Daily spin limit" hint="Spins per player per day">
        <TextField.Root
          aria-label="Daily spin limit"
          inputMode="numeric"
          disabled={form.noDailyLimit}
          value={form.dailySpinLimit}
          onChange={(e) => setForm({ ...form, dailySpinLimit: e.target.value })}
        />
        <Text as="label" size="2">
          <Flex gap="2" align="center">
            <Checkbox
              checked={form.noDailyLimit}
              onCheckedChange={(checked) => setForm({ ...form, noDailyLimit: checked === true })}
            />
            No daily limit
          </Flex>
        </Text>
        <FieldError errors={errors} path="dailySpinLimit" />
      </Field>
      {numberInput('promoTextLimit', 'Promo text limit (characters)')}
      {numberInput('wheelTextLimit', 'Wheel text limit (characters)')}
      <Field
        label="Theme"
        hint={
          doubleWheel ? 'Themes without a double wheel need a single-wheel reveal.' : undefined
        }>
        <RadioGroup.Root
          aria-label="Theme"
          value={form.theme}
          onValueChange={(theme) => setForm({ ...form, theme: theme as S2WTheme })}>
          {S2W_THEMES.map((theme) => (
            <RadioGroup.Item
              key={theme}
              value={theme}
              disabled={doubleWheel && !supportsDoubleWheel(theme)}>
              {theme}
            </RadioGroup.Item>
          ))}
        </RadioGroup.Root>
        <FieldError errors={errors} path="theme" />
      </Field>
      <Field
        label="Prize reveal"
        hint={
          supportsDoubleWheel(form.theme) ? undefined : 'This theme only supports a single wheel.'
        }>
        <RadioGroup.Root
          aria-label="Prize reveal"
          value={form.prizeRevealMethod}
          onValueChange={(method) =>
            setForm({ ...form, prizeRevealMethod: method as PrizeRevealMethod })
          }>
          {PRIZE_REVEAL_METHODS.map((method) => (
            <RadioGroup.Item
              key={method}
              value={method}
              disabled={
                method === PrizeRevealMethod.DOUBLE_WHEEL_PRIZE_REVEAL &&
                !supportsDoubleWheel(form.theme)
              }>
              {prizeRevealMethodLabel(method)}
            </RadioGroup.Item>
          ))}
        </RadioGroup.Root>
        <FieldError errors={errors} path="prizeRevealMethod" />
      </Field>
    </EditDrawerFrame>
  )
}
